import React, { useEffect, useMemo, useState } from 'react';
import SEO from '../components/SEO';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import AppClassesManager from '../components/admin/AppClassesManager';
import VocabularyDatabaseManager from '../components/admin/VocabularyDatabaseManager';
import StudentManager from '../components/admin/StudentManager';
import PortalClassesManager from '../components/admin/PortalClassesManager';
import RecordingsManager from '../components/admin/RecordingsManager';
import MaterialsManager from '../components/admin/MaterialsManager';
import AnnouncementsManager from '../components/admin/AnnouncementsManager';
import CertificatesManager from '../components/admin/CertificatesManager';
import PaymentsManager from '../components/admin/PaymentsManager';
import ReminderSettingsManager from '../components/admin/ReminderSettingsManager';
import {
  Users,
  MessageSquare,
  BookOpen,
  Settings,
  LogOut,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Mail,
  Phone,
  GraduationCap,
  Languages,
  Clock,
  CheckCircle,
  XCircle,
  Database,
  Award,
  Bell,
} from 'lucide-react';

interface Registration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  age: number;
  instagram?: string | null;
  current_level: string;
  selected_course: string;
  start_date?: string | null;
  payment_method: string;
  status: string;
  notes?: string | null;
  created_at: string;
  newsletter_opt_in?: boolean | null;
  withdrawal_consent?: boolean | null;
}

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  course?: string | null;
  message: string;
  status: string;
  notes?: string | null;
  created_at: string;
}

interface Course {
  id: string;
  level: string;
  title: string;
  subtitle?: string | null;
  description: string;
  duration: string;
  sessions: string;
  frequency?: string | null;
  color: string;
  books: string[];
  skills: string[];
  is_active: boolean;
  max_participants: number;
  price_monthly: number;
  price_full: number;
  image_url?: string | null;
  start_label?: string | null;
}

interface VocabularySuggestion {
  id: string;
  submitted_by_device_id: string | null;
  arabic: string;
  german: string;
  notes?: string | null;
  status: string;
  created_at: string;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
}

type SelectedItem = Registration | ContactSubmission | null;
type ModalType = 'view' | 'confirm';

/** "Gesehen"-Marker pro Bereich (localStorage, pro Admin-Browser) */
type SeenKey = 'registrations' | 'contacts' | 'vocabularySuggestions';
const SEEN_STORAGE_KEY = 'arabicroots:admin-seen:v1';

const loadSeenMap = (): Partial<Record<SeenKey, string>> => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

type ActiveTab =
  | 'overview'
  | 'registrations'
  | 'contacts'
  | 'courses'
  | 'appClasses'
  | 'vocabularySuggestions'
  | 'vocabularyDatabase'
  | 'portalStudents'
  | 'portalClasses'
  | 'portalRecordings'
  | 'portalMaterials'
  | 'portalAnnouncements'
  | 'portalReminders'
  | 'portalCertificates'
  | 'portalPayments'
  | 'settings';

const AdminDashboardPage = () => {
  const { user, signOut } = useAuth();

  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);

  const [loading, setLoading] = useState(true);

  const [selectedItem] = useState<SelectedItem>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<ModalType>('view');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  const [adminError, setAdminError] = useState('');

  const [registrationSearch, setRegistrationSearch] = useState('');
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState('all');

  const [contactSearch, setContactSearch] = useState('');
  const [contactStatusFilter, setContactStatusFilter] = useState('all');

  const [savingCell, setSavingCell] = useState<string | null>(null);

  const [vocabularySuggestions, setVocabularySuggestions] = useState<VocabularySuggestion[]>([]);
  const [suggestionStatusFilter, setSuggestionStatusFilter] = useState('all');
  const [suggestionSearch, setSuggestionSearch] = useState('');

  // Kurs-Bearbeitung (Website-Kurse)
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [courseForm, setCourseForm] = useState<Record<string, string | boolean>>({});
  const [savingCourse, setSavingCourse] = useState(false);

  // Einstellungen: Passwort ändern
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // "Neu"-Badges: alles nach dem gespeicherten Zeitstempel gilt als ungesehen
  const [seenMap, setSeenMap] = useState<Partial<Record<SeenKey, string>>>(loadSeenMap);

  const markSeen = (key: SeenKey) => {
    setSeenMap(prev => {
      const next = { ...prev, [key]: new Date().toISOString() };
      try {
        localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage nicht verfügbar — Badges funktionieren dann nur pro Sitzung
      }
      return next;
    });
  };

  const isUnseen = (key: SeenKey, createdAt: string) =>
    !seenMap[key] || createdAt > (seenMap[key] as string);

  const newRegistrationsCount = useMemo(
    () => registrations.filter(r => isUnseen('registrations', r.created_at)).length,
    [registrations, seenMap] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const newContactsCount = useMemo(
    () => contacts.filter(c => isUnseen('contacts', c.created_at)).length,
    [contacts, seenMap] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const newSuggestionsCount = useMemo(
    () => vocabularySuggestions.filter(v => isUnseen('vocabularySuggestions', v.created_at)).length,
    [vocabularySuggestions, seenMap] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const badgeCounts: Partial<Record<string, number>> = {
    registrations: newRegistrationsCount,
    contacts: newContactsCount,
    vocabularySuggestions: newSuggestionsCount,
  };

  // Beim Öffnen eines Tabs den Bereich als gesehen markieren → Badge verschwindet
  useEffect(() => {
    if (loading) return;
    if (activeTab === 'registrations' || activeTab === 'contacts' || activeTab === 'vocabularySuggestions') {
      markSeen(activeTab);
    }
  }, [activeTab, loading]);

  useEffect(() => {
    loadData();
  }, []);

  const cardClass =
    'bg-white rounded-2xl border border-gray-200 shadow-sm';
  const inputClass =
    'w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000]/50 outline-none transition-all duration-200 bg-white text-sm';
  const secondaryBtn =
    'inline-flex items-center px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors duration-200 font-medium text-sm';
  const primaryBtn =
    'inline-flex items-center px-4 py-2 rounded-xl bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm';
  const dangerBtn =
    'inline-flex items-center px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 font-medium text-sm';

  const loadData = async () => {
    setLoading(true);
    try {
      const [regResponse, contactResponse, courseResponse, suggestionResponse] = await Promise.all([
  supabase.from('registrations').select('*').order('created_at', { ascending: false }),
  supabase.from('contact_submissions').select('*').order('created_at', { ascending: false }),
  supabase.from('courses').select('*').order('level', { ascending: true }),
  supabase.from('vocabulary_suggestions').select('*').order('created_at', { ascending: false }),
]);

      if (regResponse.error) throw regResponse.error;
      if (contactResponse.error) throw contactResponse.error;
      if (courseResponse.error) throw courseResponse.error;
      if (suggestionResponse.error) throw suggestionResponse.error;

      // Alte Einträge mit DB-Default "pending" wie "Neu" behandeln
      setRegistrations(
        ((regResponse.data ?? []) as Registration[]).map(r =>
          r.status === 'pending' ? { ...r, status: 'new' } : r
        )
      );
      setContacts((contactResponse.data ?? []) as ContactSubmission[]);
      setCourses((courseResponse.data ?? []) as Course[]);
      setVocabularySuggestions((suggestionResponse.data ?? []) as VocabularySuggestion[]);
    } catch (error) {
      if (import.meta.env.DEV) console.error('Fehler beim Laden:', error);
      setAdminError('Fehler beim Laden der Dashboard-Daten. Bitte Seite neu laden.');
    } finally {
      setLoading(false);
    }
  };

  const showConfirmDialog = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setModalType('confirm');
    setShowModal(true);
  };

  const handleConfirm = () => {
    if (confirmAction) confirmAction();
    setShowModal(false);
    setConfirmAction(null);
  };

  const handleLogout = () => {
    showConfirmDialog('Möchten Sie sich wirklich abmelden?', () => {
      signOut();
    });
  };

 const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'waitlist':
        return 'bg-purple-100 text-purple-800 border border-purple-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'open':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'replied':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'done':
        return 'bg-gray-100 text-gray-800 border border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getPaymentLabel = (paymentMethod: string) => {
    switch (paymentMethod) {
      case 'monthly':
        return 'Monatlich';
      case 'full':
        return 'Einmalzahlung';
      default:
        return paymentMethod;
    }
  };

  const updateRegistrationField = async (
    id: string,
    field: keyof Pick<Registration, 'status'| 'notes'>,
    value: string | null
  ) => {
    try {
      setSavingCell(id + field);

      const { error } = await supabase
        .from('registrations')
        .update({ [field]: value })
        .eq('id', id);

      if (error) throw error;

      setRegistrations(prev =>
        prev.map(r =>
          r.id === id ? { ...r, [field]: value } : r
        )
      );
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      setAdminError('Fehler beim Speichern. Bitte erneut versuchen.');
    } finally {
      setSavingCell(null);
    }
  };

  const updateContactField = async (
    id: string,
    field: keyof Pick<ContactSubmission, 'status' | 'notes'>,
    value: string | null
  ) => {
    try {
      setSavingCell(id + field);

      const { error } = await supabase
        .from('contact_submissions')
        .update({ [field]: value })
        .eq('id', id);

      if (error) throw error;

      setContacts(prev =>
        prev.map(c =>
          c.id === id ? { ...c, [field]: value } : c
        )
      );
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      setAdminError('Fehler beim Speichern. Bitte erneut versuchen.');
    } finally {
      setSavingCell(null);
    }
  };

  const updateVocabularySuggestionStatus = async (id: string, status: 'pending' | 'approved' | 'rejected') => {
  try {
    setSavingCell(id + status);

    const { error } = await supabase
      .from('vocabulary_suggestions')
      .update({
        status,
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id ?? null,
      })
      .eq('id', id);

    if (error) throw error;

    setVocabularySuggestions(prev =>
      prev.map(item =>
        item.id === id
          ? {
              ...item,
              status,
              reviewed_at: new Date().toISOString(),
              reviewed_by: user?.id ?? null,
            }
          : item
      )
    );
  } catch (error) {
    if (import.meta.env.DEV) console.error(error);
    setAdminError('Fehler beim Aktualisieren des Vorschlags. Bitte erneut versuchen.');
  } finally {
    setSavingCell(null);
  }
};

  const deleteVocabularySuggestion = async (id: string) => {
  try {
    setSavingCell(id + 'delete');

    const { error } = await supabase
      .from('vocabulary_suggestions')
      .delete()
      .eq('id', id);

    if (error) throw error;

    setVocabularySuggestions(prev => prev.filter(item => item.id !== id));
  } catch (error) {
    if (import.meta.env.DEV) console.error(error);
    setAdminError('Fehler beim Löschen des Vorschlags. Bitte erneut versuchen.');
  } finally {
    setSavingCell(null);
  }
};
  
 const deleteRegistration = async (id: string) => {
  try {
    const { error } = await supabase
      .from('registrations')
      .delete()
      .eq('id', id);

    if (error) throw error;

    setRegistrations(prev => prev.filter(r => r.id !== id));
  } catch (err) {
    if (import.meta.env.DEV) console.error(err);
  }
};

const deleteContact = async (id: string) => {
  try {
    const { error } = await supabase.from('contact_submissions').delete().eq('id', id);
    if (error) throw error;
    setContacts(prev => prev.filter(c => c.id !== id));
  } catch (err) {
    if (import.meta.env.DEV) console.error(err);
    setAdminError('Fehler beim Löschen des Kontakts. Bitte erneut versuchen.');
  }
};

  // ─── Kurse bearbeiten (Website-Inhalte) ────────────────────────────
  const openCourseEditor = (course: Course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title ?? '',
      subtitle: course.subtitle ?? '',
      description: course.description ?? '',
      duration: course.duration ?? '',
      sessions: course.sessions ?? '',
      frequency: course.frequency ?? '',
      books: (course.books ?? []).join('\n'),
      skills: (course.skills ?? []).join('\n'),
      image_url: course.image_url ?? '',
      start_label: course.start_label ?? 'Sobald Gruppe voll ist',
      price_monthly: String(course.price_monthly ?? ''),
      price_full: String(course.price_full ?? ''),
      max_participants: String(course.max_participants ?? 8),
      is_active: course.is_active,
    });
  };

  const saveCourse = async () => {
    if (!editingCourse) return;
    setSavingCourse(true);
    try {
      const toList = (v: string) => v.split('\n').map(s => s.trim()).filter(Boolean);
      const payload = {
        title: String(courseForm.title).trim(),
        subtitle: String(courseForm.subtitle).trim() || null,
        description: String(courseForm.description).trim(),
        duration: String(courseForm.duration).trim(),
        sessions: String(courseForm.sessions).trim(),
        frequency: String(courseForm.frequency).trim() || null,
        books: toList(String(courseForm.books)),
        skills: toList(String(courseForm.skills)),
        image_url: String(courseForm.image_url).trim() || null,
        start_label: String(courseForm.start_label).trim() || 'Sobald Gruppe voll ist',
        price_monthly: parseFloat(String(courseForm.price_monthly).replace(',', '.')) || 0,
        price_full: parseFloat(String(courseForm.price_full).replace(',', '.')) || 0,
        max_participants: parseInt(String(courseForm.max_participants), 10) || 8,
        is_active: Boolean(courseForm.is_active),
      };
      const { error } = await supabase.from('courses').update(payload).eq('id', editingCourse.id);
      if (error) throw error;
      setCourses(prev => prev.map(c => (c.id === editingCourse.id ? { ...c, ...payload } : c)));
      setEditingCourse(null);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      setAdminError('Fehler beim Speichern des Kurses. Bitte erneut versuchen.');
    } finally {
      setSavingCourse(false);
    }
  };

  // ─── Einstellungen ─────────────────────────────────────────────────
  const exportCsv = (rows: Record<string, unknown>[], filename: string) => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.join(';'), ...rows.map(r => headers.map(h => escape(r[h])).join(';'))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const changeAdminPassword = async () => {
    setPasswordMessage('');
    if (newPassword.length < 8) {
      setPasswordMessage('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (newPassword !== newPasswordRepeat) {
      setPasswordMessage('Die Passwörter stimmen nicht überein.');
      return;
    }
    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword('');
      setNewPasswordRepeat('');
      setPasswordMessage('Passwort erfolgreich geändert.');
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      setPasswordMessage('Fehler beim Ändern des Passworts. Bitte erneut versuchen.');
    } finally {
      setChangingPassword(false);
    }
  };

  const filteredRegistrations = useMemo(() => {
  return registrations.filter(r => {
    const matchesSearch =
      `${r.first_name} ${r.last_name}`.toLowerCase().includes(registrationSearch.toLowerCase()) ||
      r.email.toLowerCase().includes(registrationSearch.toLowerCase());

    const matchesStatus =
      registrationStatusFilter === 'all' || r.status === registrationStatusFilter;

    return matchesSearch && matchesStatus;
  });
}, [registrations, registrationSearch, registrationStatusFilter]);

  const filteredContacts = useMemo(() => {
    return contacts.filter(c => {
      const matchesSearch =
        c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.email.toLowerCase().includes(contactSearch.toLowerCase());

      const matchesStatus =
        contactStatusFilter === 'all' || c.status === contactStatusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [contacts, contactSearch, contactStatusFilter]);

  const filteredVocabularySuggestions = useMemo(() => {
  return vocabularySuggestions.filter((item) => {
    const query = suggestionSearch.toLowerCase();

    const matchesSearch =
      item.arabic.toLowerCase().includes(query) ||
      item.german.toLowerCase().includes(query) ||
      (item.notes || '').toLowerCase().includes(query);

    const matchesStatus =
      suggestionStatusFilter === 'all' || item.status === suggestionStatusFilter;

    return matchesSearch && matchesStatus;
  });
}, [vocabularySuggestions, suggestionSearch, suggestionStatusFilter]);


    if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEO
          title="Admin Dashboard"
          description="Arabicroots Admin Dashboard"
          url="/admin/dashboard"
          noIndex={true}
        />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#970000] mx-auto mb-4"></div>
          <p className="text-gray-500">Dashboard wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Admin Dashboard"
        description="Arabicroots Admin Dashboard - Verwaltung von Anmeldungen, Kontakten und Kursen"
        url="/admin/dashboard"
        noIndex={true}
      />

      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="/LogoHeader4.png"
              alt="Arabicroots Logo"
              className="w-10 h-10 mr-3 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div>
              <h1 className="text-base font-bold text-gray-900">Arabicroots Admin</h1>
              <p className="text-xs text-gray-400">Interner Bereich</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className={secondaryBtn}>
              <LogOut className="h-4 w-4 mr-2" />
              Abmelden
            </button>
          </div>
        </div>
      </header>

      {adminError && (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
              <p className="text-sm text-red-700">{adminError}</p>
            </div>
            <button onClick={() => setAdminError('')} className="text-red-400 hover:text-red-600 transition-colors text-xs font-medium shrink-0">
              Schließen
            </button>
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col xl:flex-row gap-8">
          <aside className="xl:w-72 shrink-0">
            <nav className={`${cardClass} p-3 sticky top-20`}>
              <ul className="space-y-2">
                {[
                  { id: 'overview', label: 'Übersicht', icon: Users },
                  { id: 'registrations', label: 'Anmeldungen', icon: Users },
                  { id: 'contacts', label: 'Kontakte', icon: MessageSquare },
                  { id: 'courses', label: 'Kurse', icon: BookOpen },
                  { id: 'appClasses', label: 'App-Klassen', icon: GraduationCap },
                  { id: 'vocabularySuggestions', label: 'Vokabel-Vorschläge', icon: Languages },
                  { id: 'vocabularyDatabase', label: 'Vokabel-Datenbank', icon: Database },
                  { id: 'portalStudents', label: 'Portal: Schüler', icon: Users },
                  { id: 'portalClasses', label: 'Portal: Klassen', icon: GraduationCap },
                  { id: 'portalRecordings', label: 'Portal: Aufzeichnungen', icon: BookOpen },
                  { id: 'portalMaterials', label: 'Portal: Materialien', icon: Database },
                  { id: 'portalAnnouncements', label: 'Portal: Ankündigungen', icon: MessageSquare },
                  { id: 'portalReminders', label: 'Portal: Live-Erinnerungen', icon: Bell },
                  { id: 'portalCertificates', label: 'Portal: Zertifikate', icon: Award },
                  { id: 'portalPayments', label: 'Zahlung & Zugang', icon: Clock },
                  { id: 'settings', label: 'Einstellungen', icon: Settings },
                ].map((item) => {
                  const badge = badgeCounts[item.id] ?? 0;
                  return (
                  <li key={item.id}>
                    <button
                      onClick={() => setActiveTab(item.id as ActiveTab)}
                      className={`w-full flex items-center px-3 py-2.5 rounded-xl text-left transition-all duration-200 text-sm ${
                        activeTab === item.id
                          ? 'bg-[#970000] text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      <span className="flex-1">{item.label}</span>
                      {badge > 0 && (
                        <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                          activeTab === item.id ? 'bg-white text-[#970000]' : 'bg-[#970000] text-white'
                        }`}>
                          {badge}
                        </span>
                      )}
                    </button>
                  </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          <main className="flex-1 space-y-6 min-w-0">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-2">
                    Übersicht
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <button onClick={() => setActiveTab('registrations')} className={`${cardClass} p-6 text-left hover:border-[#970000]/30 transition-colors`}>
                    <div className="flex items-center">
                      <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                        <Users className="h-7 w-7 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm text-gray-500">Anmeldungen</p>
                        <div className="flex items-center gap-2">
                          <p className="text-3xl font-bold text-gray-900">{registrations.length}</p>
                          {newRegistrationsCount > 0 && (
                            <span className="inline-flex items-center rounded-full bg-[#970000] px-2 py-0.5 text-[11px] font-bold text-white">
                              +{newRegistrationsCount} neu
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  <button onClick={() => setActiveTab('contacts')} className={`${cardClass} p-6 text-left hover:border-[#970000]/30 transition-colors`}>
                    <div className="flex items-center">
                      <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center">
                        <MessageSquare className="h-7 w-7 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm text-gray-500">Kontakte</p>
                        <div className="flex items-center gap-2">
                          <p className="text-3xl font-bold text-gray-900">{contacts.length}</p>
                          {newContactsCount > 0 && (
                            <span className="inline-flex items-center rounded-full bg-[#970000] px-2 py-0.5 text-[11px] font-bold text-white">
                              +{newContactsCount} neu
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  <button onClick={() => setActiveTab('courses')} className={`${cardClass} p-6 text-left hover:border-[#970000]/30 transition-colors`}>
                    <div className="flex items-center">
                      <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center">
                        <BookOpen className="h-7 w-7 text-purple-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm text-gray-500">Aktive Kurse</p>
                        <p className="text-3xl font-bold text-gray-900">
                          {courses.filter((c) => c.is_active).length}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Letzte Aktivitäten */}
                <div className={`${cardClass} p-6`}>
                  <h3 className="text-xl font-semibold text-gray-900 mb-5">
                    Letzte Aktivitäten
                  </h3>

                  <div className="space-y-3">
                    {registrations.slice(0, 5).map((r) => {
                      const unseen = isUnseen('registrations', r.created_at);
                      return (
                      <div
                        key={r.id}
                        className={`flex justify-between items-center p-3 rounded-xl border ${unseen ? 'border-[#970000]/30 bg-[#970000]/[0.03]' : 'border-gray-100'}`}
                      >
                        <div className="flex items-center gap-3">
                          {unseen && <span className="h-2 w-2 rounded-full bg-[#970000] shrink-0" aria-label="Neu" />}
                          <div>
                            <p className="font-medium">
                              {r.first_name} {r.last_name}
                              {unseen && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-[#970000]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#970000]">
                                  Neu
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {r.selected_course}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {formatDate(r.created_at)}
                        </span>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'vocabularySuggestions' && (
  <div className="space-y-6">
    <div>
      <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-2">
        App
      </p>
      <h2 className="text-3xl font-bold text-gray-900">Vokabel-Vorschläge</h2>
      <p className="text-gray-500">
        Eingereichte Wörter aus der App prüfen und markieren.
      </p>
    </div>

    <div className={cardClass + " p-4 grid md:grid-cols-3 gap-4"}>
      <input
        placeholder="Suche nach Arabisch, Deutsch oder Notiz..."
        value={suggestionSearch}
        onChange={(e) => setSuggestionSearch(e.target.value)}
        className={inputClass}
      />

      <select
        value={suggestionStatusFilter}
        onChange={(e) => setSuggestionStatusFilter(e.target.value)}
        className={inputClass}
      >
        <option value="all">Alle Status</option>
        <option value="pending">Offen</option>
        <option value="approved">Angenommen</option>
        <option value="rejected">Abgelehnt</option>
      </select>

      <button onClick={loadData} className={primaryBtn}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Reload
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className={`${cardClass} p-6`}>
        <div className="flex items-center">
          <div className="w-14 h-14 rounded-2xl bg-yellow-50 flex items-center justify-center">
            <Clock className="h-7 w-7 text-yellow-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm text-gray-500">Offen</p>
            <p className="text-3xl font-bold text-gray-900">
              {vocabularySuggestions.filter((item) => item.status === 'pending').length}
            </p>
          </div>
        </div>
      </div>

      <div className={`${cardClass} p-6`}>
        <div className="flex items-center">
          <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center">
            <CheckCircle className="h-7 w-7 text-green-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm text-gray-500">Angenommen</p>
            <p className="text-3xl font-bold text-gray-900">
              {vocabularySuggestions.filter((item) => item.status === 'approved').length}
            </p>
          </div>
        </div>
      </div>

      <div className={`${cardClass} p-6`}>
        <div className="flex items-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
            <XCircle className="h-7 w-7 text-red-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm text-gray-500">Abgelehnt</p>
            <p className="text-3xl font-bold text-gray-900">
              {vocabularySuggestions.filter((item) => item.status === 'rejected').length}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className={cardClass + " overflow-auto"}>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="p-4 text-left">Vokabel</th>
            <th>Notiz</th>
            <th>Status</th>
            <th>Gerät</th>
            <th>Eingereicht</th>
            <th>Aktionen</th>
          </tr>
        </thead>

        <tbody>
          {filteredVocabularySuggestions.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50 border-t border-gray-100">
              <td className="p-4 min-w-[240px]">
  <div className="rounded-xl bg-white border border-gray-200 px-4 py-3">
    <div className="text-3xl font-bold text-gray-900 text-right leading-relaxed" dir="rtl">
      {item.arabic}
    </div>
    <div className="mt-2 pt-2 border-t border-gray-100 font-semibold text-[#970000]">
      {item.german}
    </div>
  </div>
</td>

              <td className="p-4 max-w-sm">
  {item.notes ? (
    <div className="rounded-2xl bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500 leading-relaxed max-h-24 overflow-auto">
      {item.notes}
    </div>
  ) : (
    <span className="text-gray-400">Keine Notiz</span>
  )}
</td>

              <td>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  item.status === 'approved'
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : item.status === 'rejected'
                      ? 'bg-red-100 text-red-800 border border-red-200'
                      : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                }`}>
                  {item.status === 'approved'
                    ? 'Angenommen'
                    : item.status === 'rejected'
                      ? 'Abgelehnt'
                      : 'Offen'}
                </span>
              </td>

              <td className="p-4 text-xs text-gray-500">
  <span className="inline-block max-w-[120px] truncate align-bottom">
    {item.submitted_by_device_id || 'unbekannt'}
  </span>
</td>

              <td className="text-xs text-gray-500">
                {formatDate(item.created_at)}
              </td>

            <td className="p-4">
  <div className="flex flex-col xl:flex-row gap-2 justify-end">
    <button
      type="button"
      disabled={savingCell === item.id + 'approved'}
      onClick={() => updateVocabularySuggestionStatus(item.id, 'approved')}
      className="inline-flex items-center justify-center px-3 py-2 rounded-full bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-semibold"
    >
      <CheckCircle className="h-4 w-4 mr-1" />
      {savingCell === item.id + 'approved' ? 'Speichert...' : 'Annehmen'}
    </button>

    <button
      type="button"
      disabled={savingCell === item.id + 'rejected'}
      onClick={() => updateVocabularySuggestionStatus(item.id, 'rejected')}
      className="inline-flex items-center justify-center px-3 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-semibold"
    >
      <XCircle className="h-4 w-4 mr-1" />
      {savingCell === item.id + 'rejected' ? 'Speichert...' : 'Ablehnen'}
    </button>

    <button
      type="button"
      disabled={savingCell === item.id + 'delete'}
      onClick={() =>
        showConfirmDialog(
          `Möchtest du den Vorschlag "${item.arabic} – ${item.german}" wirklich löschen?`,
          () => deleteVocabularySuggestion(item.id)
        )
      }
      className="inline-flex items-center justify-center px-3 py-2 rounded-full bg-gray-900 text-white hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-semibold"
    >
      <Trash2 className="h-4 w-4 mr-1" />
      {savingCell === item.id + 'delete' ? 'Löscht...' : 'Löschen'}
    </button>
  </div>
</td>
            </tr>
          ))}

          {filteredVocabularySuggestions.length === 0 && (
            <tr>
              <td colSpan={6} className="p-6 text-center text-gray-500">
                Keine Vokabel-Vorschläge gefunden.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
)}

          {/* ================= CONTACTS ================= */}
{activeTab === 'contacts' && (
  <div className="space-y-6">
    <div className={cardClass + " p-4 grid md:grid-cols-3 gap-4"}>
      <input
        placeholder="Suche..."
        value={contactSearch}
        onChange={(e) => setContactSearch(e.target.value)}
        className={inputClass}
      />

      <select
        value={contactStatusFilter}
        onChange={(e) => setContactStatusFilter(e.target.value)}
        className={inputClass}
      >
        <option value="all">Status</option>
        <option value="open">Offen</option>
        <option value="replied">Beantwortet</option>
        <option value="done">Erledigt</option>
      </select>

      <button onClick={loadData} className={primaryBtn}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Reload
      </button>
    </div>

    <div className={cardClass + " overflow-auto"}>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
  <tr>
    <th className="p-4 text-left">Name</th>
    <th>Betreff</th>
    <th>Nachricht</th>
    <th>Status</th>
    <th>Notizen</th>
    <th>Aktionen</th>
  </tr>
</thead>

        <tbody>
          {filteredContacts.map((c) => (
            <tr key={c.id} className="hover:bg-gray-50">
              <td className="p-4">
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-gray-500">{c.email}</div>
              </td>

              <td>{c.subject}</td>
              <td className="max-w-xs">
  <div className="max-h-20 overflow-auto text-xs">
    {c.message}
  </div>
</td>

              <td>
                <select
                  value={c.status}
                  onChange={(e) => updateContactField(c.id, 'status', e.target.value)}
                  className={`px-2 py-1 rounded-full ${getStatusColor(c.status)}`}
                >
                  <option value="open">Offen</option>
                  <option value="replied">Beantwortet</option>
                  <option value="done">Erledigt</option>
                </select>
              </td>

              <td>
                <input
                  value={c.notes || ''}
                  onChange={(e) => updateContactField(c.id, 'notes', e.target.value)}
                  className="border rounded px-2 py-1 w-full"
                  placeholder="Notiz..."
                />
              </td>

              <td className="p-4">
                <div className="flex items-center gap-2">
                  <a
                    href={`mailto:${c.email}?subject=${encodeURIComponent(`Re: ${c.subject} – Arabicroots`)}&body=${encodeURIComponent(
                      `Hallo ${c.name},\n\n\n\nViele Grüße\nDein Arabicroots-Team\n\n────────────────────\nDeine Nachricht vom ${formatDate(c.created_at)}:\n${c.message}`
                    )}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors text-xs font-semibold whitespace-nowrap"
                    title={`Antwort an ${c.email} verfassen`}
                  >
                    <Mail size={13} />
                    Antworten
                  </a>

                  <button
                    onClick={() =>
                      showConfirmDialog(
                        `Kontakt von ${c.name} löschen?`,
                        () => deleteContact(c.id)
                      )
                    }
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors shrink-0"
                    title="Kontakt löschen"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}

          {filteredContacts.length === 0 && (
            <tr>
              <td colSpan={7} className="p-6 text-center text-gray-500">
                Keine Kontakte gefunden.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
)}
                     

            {activeTab === 'courses' && (
  <div className="space-y-6">
    <div>
      <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-2">
        Website
      </p>
      <h2 className="text-3xl font-bold text-gray-900">Kurse</h2>
      <p className="text-gray-500">
        Diese Inhalte werden live auf der Website angezeigt — Kurskarten der Startseite und Kursauswahl der Anmeldeseite.
        Preise, Dauer, Bücher &amp; Co. änderst du hier, ohne den Code anzufassen.
      </p>
    </div>

    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
      {courses.map((course) => (
        <div key={course.id} className={cardClass + " p-6 flex flex-col"}>
          <div className="flex items-center justify-between mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold tracking-[0.1em] uppercase bg-[#970000]/[0.08] text-[#970000]">
              {course.level}
            </span>
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                course.is_active
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-gray-100 text-gray-500 border border-gray-200'
              }`}
            >
              {course.is_active ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>

          <h3 className="text-lg font-semibold text-gray-900">{course.title}</h3>
          {course.subtitle && <p className="text-xs text-gray-400 mb-2">{course.subtitle}</p>}
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{course.description}</p>

          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-4">
            <p><strong>Dauer:</strong><br />{course.duration}</p>
            <p><strong>Sessions:</strong><br />{course.sessions}</p>
            <p><strong>Monatlich:</strong><br />€{course.price_monthly}</p>
            <p><strong>Einmalig:</strong><br />€{course.price_full}</p>
          </div>

          <button
            onClick={() => openCourseEditor(course)}
            className={primaryBtn + ' mt-auto justify-center'}
          >
            Bearbeiten
          </button>
        </div>
      ))}
    </div>

    {/* Kurs-Editor */}
    {editingCourse && (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
        <div className="flex min-h-full items-start justify-center py-6">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#970000] mb-1">
                  {editingCourse.level}
                </p>
                <h3 className="text-xl font-bold text-gray-900">Kurs bearbeiten</h3>
                <p className="text-sm text-gray-500">Änderungen sind nach dem Speichern sofort auf der Website sichtbar.</p>
              </div>
              <button
                onClick={() => setEditingCourse(null)}
                className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors shrink-0"
                aria-label="Schließen"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Titel (Startseiten-Karte)</span>
                <input className={inputClass + ' mt-1'} value={String(courseForm.title)} onChange={e => setCourseForm(f => ({ ...f, title: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Untertitel / Bücher (Anmeldeseite)</span>
                <input className={inputClass + ' mt-1'} value={String(courseForm.subtitle)} onChange={e => setCourseForm(f => ({ ...f, subtitle: e.target.value }))} />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-semibold text-gray-600">Beschreibung</span>
                <textarea rows={2} className={inputClass + ' mt-1'} value={String(courseForm.description)} onChange={e => setCourseForm(f => ({ ...f, description: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Dauer (z.B. „ca. 35 Wochen“)</span>
                <input className={inputClass + ' mt-1'} value={String(courseForm.duration)} onChange={e => setCourseForm(f => ({ ...f, duration: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Live-Sessions gesamt (z.B. „ca. 70 Live-Sessions“)</span>
                <input className={inputClass + ' mt-1'} value={String(courseForm.sessions)} onChange={e => setCourseForm(f => ({ ...f, sessions: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Rhythmus (z.B. „2x die Woche (je ca. 60min)“)</span>
                <input className={inputClass + ' mt-1'} value={String(courseForm.frequency)} onChange={e => setCourseForm(f => ({ ...f, frequency: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Start-Anzeige</span>
                <input className={inputClass + ' mt-1'} value={String(courseForm.start_label)} onChange={e => setCourseForm(f => ({ ...f, start_label: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Preis monatlich (€)</span>
                <input inputMode="decimal" className={inputClass + ' mt-1'} value={String(courseForm.price_monthly)} onChange={e => setCourseForm(f => ({ ...f, price_monthly: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Preis einmalig (€)</span>
                <input inputMode="decimal" className={inputClass + ' mt-1'} value={String(courseForm.price_full)} onChange={e => setCourseForm(f => ({ ...f, price_full: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Bild-URL (Startseiten-Karte)</span>
                <input className={inputClass + ' mt-1'} value={String(courseForm.image_url)} onChange={e => setCourseForm(f => ({ ...f, image_url: e.target.value }))} placeholder="/buchstaben.png" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Max. Teilnehmer</span>
                <input inputMode="numeric" className={inputClass + ' mt-1'} value={String(courseForm.max_participants)} onChange={e => setCourseForm(f => ({ ...f, max_participants: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Bücher (eins pro Zeile)</span>
                <textarea rows={4} className={inputClass + ' mt-1'} value={String(courseForm.books)} onChange={e => setCourseForm(f => ({ ...f, books: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600">Das lernst du (eins pro Zeile)</span>
                <textarea rows={4} className={inputClass + ' mt-1'} value={String(courseForm.skills)} onChange={e => setCourseForm(f => ({ ...f, skills: e.target.value }))} />
              </label>
              <label className="flex items-center gap-2 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(courseForm.is_active)}
                  onChange={e => setCourseForm(f => ({ ...f, is_active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-[#970000] focus:ring-[#970000]/20"
                />
                <span className="text-sm text-gray-700">Kurs aktiv (auf der Website sichtbar)</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button onClick={() => setEditingCourse(null)} className={secondaryBtn}>
                Abbrechen
              </button>
              <button onClick={saveCourse} disabled={savingCourse} className={primaryBtn + ' disabled:opacity-50'}>
                {savingCourse ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
)}

            {activeTab === 'appClasses' && (
              <AppClassesManager
                user={user}
                cardClass={cardClass}
                inputClass={inputClass}
                primaryBtn={primaryBtn}
                secondaryBtn={secondaryBtn}
                showConfirmDialog={showConfirmDialog}
                formatDate={formatDate}
              />
            )}

            {activeTab === 'vocabularyDatabase' && (
              <VocabularyDatabaseManager
                cardClass={cardClass}
                inputClass={inputClass}
                primaryBtn={primaryBtn}
                secondaryBtn={secondaryBtn}
                showConfirmDialog={showConfirmDialog}
                formatDate={formatDate}
              />
            )}

            {activeTab === 'registrations' && (
  <div className="space-y-6">
    <div>
      <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-2">
        Anmeldungen
      </p>
      <h2 className="text-3xl font-bold text-gray-900">Kursanmeldungen</h2>
    </div>

    <div className={cardClass + " p-4 grid md:grid-cols-4 gap-4"}>
      <input
        placeholder="Suche..."
        value={registrationSearch}
        onChange={(e) => setRegistrationSearch(e.target.value)}
        className={inputClass}
      />

      <select
        value={registrationStatusFilter}
        onChange={(e) => setRegistrationStatusFilter(e.target.value)}
        className={inputClass}
      >
        <option value="all">Status</option>
        <option value="new">Neu</option>
        <option value="contacted">Kontaktiert</option>
        <option value="confirmed">Bestätigt</option>
        <option value="waitlist">Warteliste</option>
        <option value="rejected">Abgelehnt</option>
        <option value="cancelled">Storniert</option>
      </select>


      <button onClick={loadData} className={primaryBtn}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Reload
      </button>
    </div>

    <div className={cardClass + " overflow-auto"}>
      <table className="w-full text-sm">
       <thead className="bg-gray-50">
  <tr>
    <th className="p-4 text-left">Name</th>
    <th>Kurs</th>
    <th>Geschlecht</th>
    <th>Alter</th>
    <th>Telefon</th>
    <th>Zahlung</th>
    <th>Status</th>
    <th>Notizen</th>
    <th>Aktionen</th>
  </tr>
</thead>

        <tbody>
  {filteredRegistrations.map((r) => (
    <tr key={r.id} className="hover:bg-gray-50">
      <td className="p-4">
        <div className="font-semibold">{r.first_name} {r.last_name}</div>
        <div className="text-xs text-gray-500">{r.email}</div>
      </td>

      <td>{r.selected_course}</td>

      <td className="text-sm">
        {r.gender === 'male' ? 'Mann' : r.gender === 'female' ? 'Frau' : r.gender}
      </td>

      <td className="text-sm">
        {r.age}
      </td>

      <td className="text-sm">
        {r.phone}
      </td>

      <td>
        <div>{getPaymentLabel(r.payment_method)}</div>
        <span
          className={`mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
            r.withdrawal_consent
              ? 'bg-green-100 text-green-800'
              : 'bg-amber-100 text-amber-800'
          }`}
          title="Zustimmung zum Leistungsbeginn vor Ablauf der Widerrufsfrist"
        >
          {r.withdrawal_consent ? 'Vorzeitiger Start: Ja' : 'Vorzeitiger Start: Nein'}
        </span>
      </td>

      <td>
        <select
          value={r.status}
          onChange={(e) => updateRegistrationField(r.id, 'status', e.target.value)}
          className={`px-2 py-1 rounded-full ${getStatusColor(r.status)}`}
        >
          <option value="new">Neu</option>
          <option value="contacted">Kontaktiert</option>
          <option value="confirmed">Bestätigt</option>
          <option value="waitlist">Warteliste</option>
          <option value="rejected">Abgelehnt</option>
          <option value="cancelled">Storniert</option>
        </select>
      </td>

      <td>
        <input
          value={r.notes || ''}
          onChange={(e) => updateRegistrationField(r.id, 'notes', e.target.value)}
          className="border rounded px-2 py-1 w-full"
          placeholder="Notiz..."
        />
      </td>

      <td className="p-4">
        <div className="flex items-center gap-2">
          <a
            href={`mailto:${r.email}?subject=${encodeURIComponent('Deine Anmeldung bei Arabicroots')}&body=${encodeURIComponent(
              `Hallo ${r.first_name},\n\nvielen Dank für deine Anmeldung zum Kurs „${r.selected_course}“.\n\n\n\nViele Grüße\nDein Arabicroots-Team`
            )}`}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-[#970000] hover:bg-[#970000]/5 hover:border-[#970000]/30 transition-colors shrink-0"
            title={`E-Mail an ${r.email} verfassen`}
          >
            <Mail size={14} />
          </a>

          <a
            href={`https://wa.me/${(r.phone || '').replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-green-600 hover:bg-green-50 hover:border-green-200 transition-colors shrink-0"
            title="WhatsApp öffnen"
          >
            <Phone size={14} />
          </a>

          <button
            onClick={() =>
              showConfirmDialog(
                `Anmeldung von ${r.first_name} ${r.last_name} löschen?`,
                () => deleteRegistration(r.id)
              )
            }
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors shrink-0"
            title="Anmeldung löschen"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  ))}

  {filteredRegistrations.length === 0 && (
    <tr>
      <td colSpan={10} className="p-6 text-center text-gray-500">
        Keine Anmeldungen gefunden.
      </td>
    </tr>
  )}
</tbody>
      </table>
    </div>
  </div>
)}

            {/* ================= PORTAL TABS ================= */}
            {activeTab === 'portalStudents' && <StudentManager />}
            {activeTab === 'portalClasses' && <PortalClassesManager />}
            {activeTab === 'portalRecordings' && <RecordingsManager />}
            {activeTab === 'portalMaterials' && <MaterialsManager />}
            {activeTab === 'portalAnnouncements' && <AnnouncementsManager />}
            {activeTab === 'portalReminders' && <ReminderSettingsManager />}
            {activeTab === 'portalCertificates' && <CertificatesManager />}
            {activeTab === 'portalPayments' && <PaymentsManager />}

            {/* ================= SETTINGS ================= */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div>
                  <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-2">
                    System
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900">Einstellungen</h2>
                </div>

                {/* Daten-Export */}
                <div className={cardClass + ' p-6'}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Daten-Export</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Lädt die aktuellen Daten als CSV herunter (z.B. für Excel oder Backups).
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => exportCsv(registrations as unknown as Record<string, unknown>[], `anmeldungen-${new Date().toISOString().slice(0, 10)}.csv`)}
                      disabled={registrations.length === 0}
                      className={primaryBtn + ' disabled:opacity-50'}
                    >
                      Anmeldungen exportieren ({registrations.length})
                    </button>
                    <button
                      onClick={() => exportCsv(contacts as unknown as Record<string, unknown>[], `kontakte-${new Date().toISOString().slice(0, 10)}.csv`)}
                      disabled={contacts.length === 0}
                      className={secondaryBtn + ' disabled:opacity-50'}
                    >
                      Kontakte exportieren ({contacts.length})
                    </button>
                  </div>
                </div>

                {/* Passwort ändern */}
                <div className={cardClass + ' p-6'}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Admin-Passwort ändern</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Ändert das Passwort des aktuell angemeldeten Admin-Kontos ({user?.email}).
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                    <input
                      type="password"
                      placeholder="Neues Passwort (min. 8 Zeichen)"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      className={inputClass}
                      autoComplete="new-password"
                    />
                    <input
                      type="password"
                      placeholder="Neues Passwort wiederholen"
                      value={newPasswordRepeat}
                      onChange={e => setNewPasswordRepeat(e.target.value)}
                      className={inputClass}
                      autoComplete="new-password"
                    />
                  </div>
                  {passwordMessage && (
                    <p className={`mt-3 text-sm ${passwordMessage.includes('erfolgreich') ? 'text-green-600' : 'text-red-600'}`}>
                      {passwordMessage}
                    </p>
                  )}
                  <button
                    onClick={changeAdminPassword}
                    disabled={changingPassword || !newPassword}
                    className={primaryBtn + ' mt-4 disabled:opacity-50'}
                  >
                    {changingPassword ? 'Ändert…' : 'Passwort ändern'}
                  </button>
                </div>

                {/* Benachrichtigungs-Marker */}
                <div className={cardClass + ' p-6'}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">„Neu“-Markierungen</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Die roten Badges an Anmeldungen, Kontakten und Vokabel-Vorschlägen verschwinden automatisch,
                    sobald du den jeweiligen Bereich öffnest. Hier kannst du sie manuell zurücksetzen.
                  </p>
                  <button
                    onClick={() => {
                      try { localStorage.removeItem(SEEN_STORAGE_KEY); } catch { /* ignorieren */ }
                      setSeenMap({});
                    }}
                    className={secondaryBtn}
                  >
                    Alle wieder als „neu“ anzeigen
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md">

            {modalType === 'confirm' && (
              <>
                <h3 className="text-lg font-semibold mb-4">
                  Bestätigung
                </h3>
                <p className="mb-6">{confirmMessage}</p>

                <div className="flex justify-end gap-3">
                  <button onClick={() => setShowModal(false)} className={secondaryBtn}>
                    Abbrechen
                  </button>
                  <button onClick={handleConfirm} className={dangerBtn}>
                    Bestätigen
                  </button>
                </div>
              </>
            )}

            {modalType === 'view' && selectedItem && (
              <>
                <h3 className="text-lg font-semibold mb-4">Details</h3>

                {'first_name' in selectedItem ? (
                  <div>
                    <p>{selectedItem.first_name} {selectedItem.last_name}</p>
                    <p>{selectedItem.email}</p>
                  </div>
                ) : (
                  <div>
                    <p>{selectedItem.name}</p>
                    <p>{selectedItem.email}</p>
                    <p>{selectedItem.message}</p>
                  </div>
                )}

                <button
                  onClick={() => setShowModal(false)}
                  className="mt-4 text-sm text-gray-500"
                >
                  Schließen
                </button>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;